import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/Review.css";

/**
 * Review Component
 * Props:
 * - initialRating: number (0-5)
 * - initialComment: string
 * - max: number (default 5)
 * - charLimit: number (default 500)
 * - submitting: boolean (controls disabled/loading state)
 * - onSubmit: ({ rating, comment }) => Promise|void
 * - onCancel: () => void (optional)
 * - autoFocusComment: boolean (default false)
 * - required: boolean (default true) – require both rating and comment
 * - minCommentChars: number (default 5)
 */
export default function Review({
  initialRating = 0,
  initialComment = "",
  max = 5,
  charLimit = 500,
  submitting = false,
  onSubmit,
  onCancel,
  autoFocusComment = false,
  required = true,
  minCommentChars = 5,
}) {
  const [rating, setRating] = useState(clamp(initialRating, 0, max));
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(initialComment.slice(0, charLimit));
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef(null);
  const commentRef = useRef(null);

  useEffect(() => {
    if (autoFocusComment && commentRef.current) {
      commentRef.current.focus();
    }
  }, [autoFocusComment]);

  const displayRating = hover || rating;

  const remaining = useMemo(
    () => Math.max(0, charLimit - comment.length),
    [charLimit, comment.length]
  );

  function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
  }

  function validate() {
    if (!required) return "";

    if (rating < 1) return "Please select a star rating.";
    if (comment.trim().length < minCommentChars) {
      return `Please enter at least ${minCommentChars} characters.`;
    }
    if (comment.length > charLimit) {
      return `Comment exceeds ${charLimit} characters.`;
    }
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched(true);
    const v = validate();
    setError(v);
    if (v) return;

    try {
      await onSubmit?.({ rating, comment: comment.trim() });
      // Optionally reset after submit; comment out if you prefer sticky values
      // setRating(0); setComment(""); setTouched(false);
    } catch (err) {
      setError(
        err?.message || "Something went wrong while submitting your review."
      );
    }
  }

  function handleKey(e) {
    // Keyboard control on the stars container
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setRating((r) => clamp(r + 1, 1, max));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setRating((r) => clamp(r - 1, 1, max));
    } else if (e.key === "0") {
      e.preventDefault();
      setRating(0);
    }
  }

  return (
    <form
      ref={formRef}
      className="review"
      onSubmit={handleSubmit}
      aria-describedby={error ? "review-error" : undefined}
    >
      <label className="review__label">Your rating</label>

      <div
        className="stars"
        role="radiogroup"
        aria-label="Star rating"
        onKeyDown={handleKey}
      >
        {Array.from({ length: max }).map((_, i) => {
          const val = i + 1;
          const checked = displayRating >= val;
          return (
            <StarButton
              key={val}
              value={val}
              checked={checked}
              selected={rating === val}
              disabled={submitting}
              onClick={() => setRating(val)}
              onMouseEnter={() => setHover(val)}
              onMouseLeave={() => setHover(0)}
            />
          );
        })}
      </div>

      <div className="review__legend" aria-live="polite">
        {displayRating > 0 ? `${displayRating}/${max}` : "No rating selected"}
      </div>

      <label htmlFor="review-comment" className="review__label">
        Comment
      </label>
      <textarea
        id="review-comment"
        ref={commentRef}
        className="review__textarea"
        placeholder="Share a few details: what you loved, what could be better…"
        maxLength={charLimit}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onBlur={() => setTouched(true)}
        disabled={submitting}
        rows={5}
        required={required}
      />

      <div className="review__meta">
        <span className="review__counter">{remaining} characters left</span>
        {touched && error && (
          <span id="review-error" className="review__error">
            {error}
          </span>
        )}
      </div>

      <div className="review__actions">
        {onCancel && (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <button type="submit" className="post-button" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit Review"}
        </button>
      </div>
    </form>
  );
}

function StarButton({
  value,
  checked,
  selected,
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
}) {
  const label = `${value} star${value > 1 ? "s" : ""}`;
  return (
    <button
      type="button"
      className={`star ${checked ? "star--on" : "star--off"} ${
        selected ? "star--selected" : ""
      }`}
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
    >
      <StarIcon filled={checked} />
    </button>
  );
}

function StarIcon({ filled }) {
  return (
    <svg
      className="star__icon"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      aria-hidden="true"
    >
      <path
        className="star__path"
        d="M12 .9l3.09 6.26 6.91 1-5 4.87 1.18 6.88L12 16.97l-6.18 3.25 1.18-6.88-5-4.87 6.91-1L12 .9z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  );
}
